import React, {useCallback, useEffect, useMemo, useState} from "react";
import {db} from '../../../firebase/FirebaseProvider'
import {
    doc,
    collection,
    getDoc,
    getDocs,
    query,
    orderBy,
    where
} from 'firebase/firestore'
import {useParams, useSearchParams} from 'react-router-dom';
import {Location, Points, PointsAccrual, Session, User} from "../../types/Types";
import {Card, ListGroup, Form} from "react-bootstrap";
import UserDetails from "../UserDetails";
import UserDetailsV2 from "../UserDetailsV2";
import UserNav from "../UserNav";
import PageTracking from "../../auth/PageTracking";
import PointsRow from "../../shared/PointsRow";
import {V2GlassPanel} from "../../shared/v2/V2Glass";
import V2YearPicker from "../../shared/v2/V2YearPicker";
import {
    calculatePointsAccruals,
    calculateTotalPoints,
    filterSessionsPerDayPerLocation
} from "../../util/points/pointsUtils";
import { logDataLoaded } from "../../util/Util";

interface Props {
    locations: Array<Location> | undefined,
    currentUser: User | undefined,
}

function UserPoints(props: Props) {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();

    const getYearsList = (): number[] => {
        const currentYear = new Date().getFullYear();
        const startYear = 2023;
        const years = [];
        for (let year = currentYear; year >= startYear; year--) {
            years.push(year);
        }
        years.push(1);
        return years;
    };

    const getYearStartAndEnd = (year: string) => {
        if(year === '1') {
            return { startOfYear: new Date("2000-01-01T00:00:00.000Z"), endOfYear: new Date("9999-01-01T00:00:00.000Z") };
        } else {
            const startOfYear = new Date(`${year}-01-01T00:00:00.000Z`);
            const endOfYear = new Date(`${year}-12-31T23:59:59.999Z`);
            return { startOfYear, endOfYear };
        }
    };

    const [totalPoints, setTotalPoints] = useState<number>(0);
    const [pointsAccruals, setPointsAccruals] = useState<Array<PointsAccrual> | undefined>(undefined);
    const [user, setUser] = useState<User | undefined>(undefined);
    const [year, setYear] = useState<string>(searchParams.get('year') || '1');
    const [isCalculating, setIsCalculating] = useState<boolean>(true);
    const isUiV2 = !!props.currentUser?.enableUiV2;

    const selectYear = (nextYear: string) => {
        setIsCalculating(true);
        setPointsAccruals(undefined);
        setYear(nextYear);
    };

    const updateYear = (event: React.ChangeEvent<HTMLSelectElement & HTMLInputElement>) => {
        selectYear(event.target.value);
    };

    const availablePointsYears = useMemo(() => {
        return Object.entries(user?.stats || {})
            .filter(([key, value]) => /^points\d{4}$/.test(key) && typeof value === "number" && value > 0)
            .map(([key]) => Number(key.substring("points".length)));
    }, [user]);

    const loadAllData = useCallback(async() => {
        if(!user || !id || !props.locations) {
            return;
        }

        setIsCalculating(true);
        setPointsAccruals(undefined);
        const startTime = Date.now();

        const { startOfYear, endOfYear } = getYearStartAndEnd(year);

        // Get the BONUS POINTS
        let bonusPoints = new Array<Points>();
        const bonusPointsOnceCollection = query(collection(db, 'points'),
            where('userId', '==', id),
            where('earnedBy', '==', 'BONUS_CHECK_IN'),
            where('recordedAt', '>', startOfYear),
            where('recordedAt', '<', endOfYear)
        );
        const bonusPointsCollectionSnapshot = await getDocs(bonusPointsOnceCollection);
        bonusPointsCollectionSnapshot.forEach((doc) => {
            let bonusPointsToAdd = doc.data() as Points;
            bonusPoints.push(bonusPointsToAdd)
        });
        logDataLoaded('bonus points', bonusPoints.length);

        // Get the SESSIONS
        let sessions: Array<Session> = [];
        const sessionOnceCollection = query(collection(db, 'sessions'),
            where('userId', '==', user.id),
            where('checkInTime', '>', startOfYear),
            where('checkInTime', '<', endOfYear),
            orderBy('checkInTime', 'asc'));
        const sessionOnceCollectionSnapshot = await getDocs(sessionOnceCollection);
        sessionOnceCollectionSnapshot.forEach((doc) => {
            let session = doc.data() as Session;
            sessions.push(session);
        });
        logDataLoaded('user points sessions', sessions.length);

        // PROCESS THE DATA SYNCHRONOUSLY - all at once
        const filteredSessions = filterSessionsPerDayPerLocation(sessions);
        const accruals = calculatePointsAccruals(filteredSessions, bonusPoints, props.locations);
        const totalPoints = calculateTotalPoints(accruals);
        
        // Update all state at once after processing is complete
        setPointsAccruals(accruals);
        setTotalPoints(totalPoints);
        setIsCalculating(false);
    }, [user, id, year, props.locations]);

    useEffect(() => {
        if (!id) return;
        
        (async () => {
            const userRef = doc(db, 'users', id);
            const docSnap = await getDoc(userRef);
            if (docSnap.exists()) {
                let userToSet = docSnap.data() as User;
                userToSet.id = docSnap.id;
                setUser(userToSet);
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!user || !props.locations) return;
        loadAllData();
    }, [user, loadAllData, props.locations]);

    const v2YearSelector = (
        <V2YearPicker value={year} onChange={selectYear} availableYears={availablePointsYears} ariaLabel="Select points year" />
    );

    const pointsRows = (
        <>
            {isCalculating && <div className="loading">Calculating...</div>}
            {!isCalculating && pointsAccruals && pointsAccruals.length > 0 && pointsAccruals.map((pointsAccrual: PointsAccrual) => (
                <PointsRow key={pointsAccrual.locationId + pointsAccrual.recordedAt} pointsAccrual={pointsAccrual} v2={isUiV2} />
            ))}
            {!isCalculating && pointsAccruals && pointsAccruals.length === 0 && <div className="loading">
                No points earned
            </div>}
        </>
    );

    return (
        <>
            <PageTracking />
            {isUiV2 ? <UserDetailsV2 currentUser={props.currentUser} user={user} locations={props.locations} detailActions={v2YearSelector} detailStatValues={{Points: Math.floor(totalPoints)}}/> : <UserDetails user={user}/>}
            {!isUiV2 && <UserNav currentUser={props.currentUser} user={user}/>}
            {isUiV2 ? <V2GlassPanel className="user-v2-points-list-card v2-glass-list-panel">
                <div className="user-v2-points-list">
                    {pointsRows}
                </div>
            </V2GlassPanel> : <Card className="mb-3">
                {!isUiV2 && <Card.Header className={"header-padding"}>
                    <div className={"d-inline-block stats-card float-left"}>
                        Points: <b>{Math.floor(totalPoints)}</b>
                    </div>
                    <div className={"d-inline-block float-right"}>
                        <Form.Control as="select" id="dropdown" value={year} onChange={updateYear}>
                            {getYearsList().map((yearOption) => (
                                <option key={yearOption} value={yearOption}>{yearOption === 1 ? 'Total' : yearOption}</option>
                            ))}
                        </Form.Control>
                    </div>
                </Card.Header>}
                <ListGroup variant="flush">{pointsRows}</ListGroup>
            </Card>}
        </>
    );
}

export default UserPoints;
